import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { format, parseJSON } from "date-fns";
import { Button, Link, Typography } from "@mui/material";
import { Investment } from "@rentablo/api/apiv1";
import { SecurityImage } from "@rentablo/components";
import AddIcon from "@icons/myicons/plus-circled.svg?react";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";
import WarningIcon from "@icons/myicons/warning-triangle-filled.svg?react";
import {
  getValueByInvestmentAndBookingType,
  toCurrencyWithoutLargeCents,
} from "@helpers";
import { useIsinValidation } from "@hooks";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { useSharedPortfolioContext } from "@providers/SharedPortfolioProvider";
import { useDisplayedInvestment } from "../useDisplayedInvestment";
import { getDisplayedQuoteValue } from "../getDisplayedQuoteValue";
import { checkIsShowModal, isQuoteOutdated } from "../useTableConfig";
import { InvestmentMetricKey } from "./investmentListFields";
import { MetricChip } from "./MetricChip";
import { METRIC_ICONS } from "./metricIcons";

const Row = styled.div(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.border.secondary}`,
  cursor: "pointer",
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.border.active}`,
    outlineOffset: -2,
  },
}));

// Stays at the left edge while the list's scroller moves the chips sideways;
// 100cqw is the visible width of that scroller.
const Top = styled.div(({ theme }) => ({
  position: "sticky",
  left: 0,
  width: "100cqw",
  display: "grid",
  gridTemplateColumns: `${theme.spacing(8)} minmax(0, 1fr)`,
  columnGap: theme.spacing(3),
}));

const IconCell = styled.div(({ theme }) => ({
  gridRow: "1 / span 2",
  paddingTop: theme.spacing(0.5),
}));

const Codes = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  columnGap: theme.spacing(3),
  minWidth: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
}));

// Starts under the name, past the icon column and its gap.
const Strip = styled.div(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  display: "flex",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0, 2, 0, 11),
}));

// Says why the price needs attention; the action sits in the chip row. A
// position without a price source but with a recent manual price isn't
// outdated and gets the line without the warning.
const QuoteNotice = styled("div", {
  shouldForwardProp: (prop) => prop !== "outdated",
})<{ outdated: boolean }>(({ theme, outdated }) => ({
  gridColumn: "2",
  marginTop: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  minWidth: 0,
  color: outdated ? theme.palette.warning.main : theme.palette.text.secondary,
}));

const nameLinkSx = {
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
  font: "inherit",
  verticalAlign: "top",
} as const;

interface Props {
  investment: Investment;
  image?: string | null;
  metricOrder: InvestmentMetricKey[];
  showLabels: boolean;
  onRowClick?: (investment: Investment) => void;
  onNameClick?: (investmentId: string) => void;
  onAddQuote?: (investment: Investment) => void;
}

export const InvestmentMobileRow = ({
  investment,
  image,
  metricOrder,
  showLabels,
  onRowClick,
  onNameClick,
  onAddQuote,
}: Props) => {
  const { t } = useTranslation();
  const { isSharedPortfolio } = useSharedPortfolioContext();
  const { isIsinRequired, isIsinValid } = useIsinValidation(investment);
  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();
  const {
    currency,
    purchaseValue,
    currentMarketValue,
    returnOnInvestmentInPercent,
    receivedTotalDividend,
  } = useDisplayedInvestment(investment);
  const name = investment.standardisedName || investment.name;
  const showAddQuote = Boolean(onAddQuote) && checkIsShowModal(investment);
  const isOutdated = isQuoteOutdated(investment);
  const lastPriceAt = investment.quote?.lastDateTime;

  const quoteValue = investment.quote
    ? getValueByInvestmentAndBookingType({
        value: getDisplayedQuoteValue(
          investment.quote,
          selectedCurrencyView === "original"
        ),
        investmentType: investment.type,
        currency: getDisplayCurrency(investment.currency),
        percentageRange: "0-100",
        quoteNotation: investment.quoteNotation,
      })
    : "–";

  const metricIcon = (key: InvestmentMetricKey) => {
    const Icon = METRIC_ICONS[key];
    return <Icon width={12} height={12} />;
  };

  const chips: Record<InvestmentMetricKey, ReactNode> = {
    today: (
      <MetricChip
        label={t("domains.investments.investmentTable.mobile.chips.today")}
        showLabel={showLabels}
        icon={metricIcon("today")}
        change={investment.quote?.changePercentAbs || undefined}
        value={quoteValue}
      />
    ),
    purchase: (
      <MetricChip
        label={t("domains.investments.investmentTable.mobile.chips.purchase")}
        showLabel={showLabels}
        icon={metricIcon("purchase")}
        value={toCurrencyWithoutLargeCents(purchaseValue, currency)}
      />
    ),
    total: (
      <MetricChip
        label={t("domains.investments.investmentTable.mobile.chips.total")}
        showLabel={showLabels}
        icon={metricIcon("total")}
        change={returnOnInvestmentInPercent ?? undefined}
        value={toCurrencyWithoutLargeCents(currentMarketValue, currency)}
      />
    ),
    dividends: receivedTotalDividend ? (
      <MetricChip
        label={t("domains.investments.investmentTable.mobile.chips.dividends")}
        showLabel={showLabels}
        icon={metricIcon("dividends")}
        value={toCurrencyWithoutLargeCents(receivedTotalDividend, currency)}
      />
    ) : null,
  };
  const codes = [investment.hasValidIsin && investment.isin, investment.wkn]
    .filter(Boolean)
    .join(" · ");

  return (
    <Row
      role="button"
      tabIndex={0}
      data-testid="investment-mobile-row"
      onClick={() => onRowClick?.(investment)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onRowClick?.(investment);
        }
      }}
    >
      <Top>
        <IconCell>
          <SecurityImage src={image} width={32} height={32} alt={name} />
        </IconCell>

        <Typography variant="body4" component="div" noWrap minWidth={0}>
          {isSharedPortfolio || !onNameClick ? (
            name
          ) : (
            <Link
              component="button"
              type="button"
              underline="hover"
              sx={nameLinkSx}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                onNameClick(String(investment.id));
              }}
            >
              {name}
            </Link>
          )}
        </Typography>

        <Codes>
          {codes && (
            <Typography variant="caption" color="textSecondary" noWrap>
              {codes}
            </Typography>
          )}
          {isIsinRequired && !isIsinValid && (
            <Typography
              component={RouterLink}
              to={`/investments/${investment.id}/edit?tab=advanced`}
              variant="caption"
              color="error"
              display="inline-flex"
              alignItems="center"
              gap={1}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              <ErrorIcon width={14} height={14} />
              {t("investmentsPage.isinMissing")}
            </Typography>
          )}
        </Codes>

        {showAddQuote && (
          <QuoteNotice
            data-testid="investment-quote-notice"
            outdated={isOutdated}
          >
            {isOutdated && (
              <WarningIcon width={14} height={14} aria-hidden="true" />
            )}
            <Typography
              variant="caption"
              component="span"
              color="inherit"
              noWrap
            >
              {lastPriceAt
                ? t("domains.investments.investmentTable.mobile.lastPrice", {
                    date: format(parseJSON(lastPriceAt), "dd.MM.yyyy"),
                  })
                : t("domains.investments.investmentTable.mobile.noPrice")}
            </Typography>
          </QuoteNotice>
        )}
      </Top>

      <Strip>
        {metricOrder.map((key) => (
          <React.Fragment key={key}>
            {chips[key]}
            {key === "today" && showAddQuote && (
              <Button
                variant="tertiary"
                size="small"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  onAddQuote?.(investment);
                }}
                sx={{ flex: "none" }}
              >
                {t("domains.investments.investmentTable.mobile.addQuote")}
              </Button>
            )}
          </React.Fragment>
        ))}
      </Strip>
    </Row>
  );
};
